/**
 * TransactionsPage
 *
 * Full-featured transaction list with:
 * - Page header with title
 * - Date range controls (quick filters + month picker)
 * - Search bar with advanced options
 * - Type filter dropdown
 * - Summary stats (Income/Expenses/Net)
 * - Transactions grouped by month and date
 * - Infinite scroll for loading more
 * - Transaction actions (edit, delete)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTransactions } from '@hooks/useTransactions';
import { useSummary } from '@hooks/useSummary';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { TransactionItem, TransactionItemSkeleton } from '@/components/transactions';
import { Input, Select, Modal, Button, PrivacyToggle } from '@/components/shared';
import { OverdueSection } from '@/components/OverdueSection';
import { db, Category, Account, Transaction, ScheduleFrequency, ScheduledTransaction } from '@services/db';
import { transactionService } from '@services/transactions';
import { parseSearchQuery } from '@services/searchQuery';
import { scheduledTransactionService } from '@services/scheduledTransactions';
import { normalizeDetectionKey, setBridgeCooldown } from '@services/scheduledBridge';
import { syncManager } from '@services/syncManager';
import { dataEvents } from '@services/dataEvents';
import { formatDateForInput, parseDateAsLocal } from '@/utils/date';
import {
  formatCurrency as formatCurrencyIntl,
  formatDate as formatDateIntl,
  formatMonthDay,
  formatMonthYear,
  formatMonthAxis,
} from '@/utils/format';
import { useLanguage, categoryDisplayName, TranslationParams } from '@/i18n';
import './TransactionsPage.css';

type TFunc = (key: string, params?: TranslationParams) => string;

// Frequency options for recurring transactions - labels are catalog keys,
// resolved with t() at render (stored enum values never change).
const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'weekly', label: 'transactions.frequency.weekly' },
  { value: 'biweekly', label: 'transactions.frequency.biweekly' },
  { value: 'monthly', label: 'transactions.frequency.monthly' },
  { value: 'yearly', label: 'transactions.frequency.yearly' },
];

// ============================================================
// Date Range Types and Constants
// ============================================================

type DateRangePreset = 'current' | '30d' | '60d' | '90d' | '6mo' | 'month';

// LocalStorage key for remembering user's preferred date range
const DATE_PRESET_STORAGE_KEY = 'ninjacash_date_preset';
const DEFAULT_DATE_PRESET: DateRangePreset = '60d';

/**
 * Get saved date preset from localStorage, defaulting to 60d
 */
function getSavedDatePreset(): DateRangePreset {
  try {
    const saved = localStorage.getItem(DATE_PRESET_STORAGE_KEY);
    if (saved && ['current', '30d', '60d', '90d', '6mo', 'month'].includes(saved)) {
      return saved as DateRangePreset;
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_DATE_PRESET;
}

/**
 * Save date preset to localStorage
 */
function saveDatePreset(preset: DateRangePreset): void {
  try {
    localStorage.setItem(DATE_PRESET_STORAGE_KEY, preset);
  } catch {
    // localStorage not available
  }
}

interface DateRange {
  start: Date | undefined;
  end: Date | undefined;
  label: string;
}

// Labels are catalog keys, resolved with t() at render.
const DATE_PRESETS: { value: DateRangePreset; label: string }[] = [
  { value: 'current', label: 'transactions.range.current' },
  { value: '30d', label: 'transactions.range.d30' },
  { value: '60d', label: 'transactions.range.d60' },
  { value: '90d', label: 'transactions.range.d90' },
  { value: '6mo', label: 'transactions.range.mo6' },
];

/**
 * Calculate date range from preset
 */
function getDateRangeFromPreset(preset: DateRangePreset, selectedMonth: { year: number; month: number } | undefined, t: TFunc): DateRange {
  const now = new Date();

  // Handle specific month selection
  if (preset === 'month' && selectedMonth) {
    const start = new Date(selectedMonth.year, selectedMonth.month, 1);
    const end = new Date(selectedMonth.year, selectedMonth.month + 1, 0, 23, 59, 59, 999);
    const monthName = formatMonthYear(start);
    return { start, end, label: monthName };
  }

  let start: Date;
  let end: Date;
  let label: string;

  switch (preset) {
    case 'current':
      // Current month (default)
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      label = formatMonthYear(start);
      break;
    case '30d':
      start = new Date(now);
      start.setDate(start.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      label = t('transactions.range.last_d30');
      break;
    case '60d':
      start = new Date(now);
      start.setDate(start.getDate() - 60);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      label = t('transactions.range.last_d60');
      break;
    case '90d':
      start = new Date(now);
      start.setDate(start.getDate() - 90);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      label = t('transactions.range.last_d90');
      break;
    case '6mo':
      start = new Date(now);
      start.setMonth(start.getMonth() - 6);
      start.setHours(0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      label = t('transactions.range.last_mo6');
      break;
    case 'month':
    default:
      // Fallback to current month if no selectedMonth
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      label = formatMonthYear(start);
      break;
  }

  return { start, end, label };
}

// ============================================================
// Helpers
// ============================================================

function formatCurrency(amount: number): string {
  // Central locale-aware formatter (utils/format.ts) - never inline Intl here
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function isBalanceAdjustment(description?: string): boolean {
  if (!description) return false;
  const lower = description.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

function getRangeDayCount(start: Date | undefined, end: Date | undefined, transactions: Transaction[]): number {
  let rangeStart = start;
  let rangeEnd = end;

  if ((!rangeStart || !rangeEnd) && transactions.length > 0) {
    const times = transactions
      .map((t) => new Date(t.date).getTime())
      .filter((time) => Number.isFinite(time));

    if (times.length > 0) {
      rangeStart = new Date(Math.min(...times));
      rangeEnd = new Date(Math.max(...times));
    }
  }

  if (!rangeStart || !rangeEnd) {
    return 1;
  }

  const startDay = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
  const endDay = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate()).getTime();
  return Math.max(1, Math.floor((endDay - startDay) / 86400000) + 1);
}

/**
 * Format date for group headers.
 * Returns "Today", "Yesterday", or full date like "Dec 23, 2025"
 */
function formatDateHeader(date: Date, t: TFunc): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Full-sentence catalog templates - never split "Today, {date}".
  if (d.getTime() === today.getTime()) {
    return t('transactions.group_today', { date: formatMonthDay(date) });
  }
  if (d.getTime() === yesterday.getTime()) {
    return t('transactions.group_yesterday', { date: formatMonthDay(date) });
  }

  return formatDateIntl(date, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Get month key from date (e.g., "2025-12")
 */
function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Format month key for display (e.g., "DECEMBER 2025")
 */
function formatMonthHeader(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  // toUpperCase is a Latin-only style (identity on CJK/Thai, so harmless);
  // if a language ever needs different casing, move this into CSS.
  return formatMonthYear(date).toUpperCase();
}

/**
 * Get date key for grouping (e.g., "2025-12-25")
 */
function getDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// ============================================================
// Types
// ============================================================

interface GroupedTransactions {
  monthKey: string;
  monthLabel: string;
  count: number;
  days: {
    dateKey: string;
    dateLabel: string;
    transactions: Transaction[];
  }[];
}

interface SearchInsightStats {
  count: number;
  income: number;
  expense: number;
  transfer: number;
  net: number;
  monthlyExpenseAverage: number;
  biweeklyExpenseAverage: number;
  monthlyIncomeAverage: number;
  biweeklyIncomeAverage: number;
  dayCount: number;
}

// ============================================================
// Transaction Actions Modal
// ============================================================

interface TransactionActionsModalProps {
  transaction: Transaction | null;
  category?: Category;
  account?: Account;
  categories: Category[];  // All categories for edit dropdown
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
  onRefresh: () => void;
}

function TransactionActionsModal({
  transaction,
  category,
  account,
  categories,
  onClose,
  onDelete,
  onRefresh,
}: TransactionActionsModalProps) {
  const { t } = useLanguage();
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editCategory, setEditCategory] = useState<string | undefined>(undefined);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<ScheduleFrequency>('monthly');
  const [schedule, setSchedule] = useState<ScheduledTransaction | null>(null);
  const [togglingRecurring, setTogglingRecurring] = useState(false);
  const [changingFrequency, setChangingFrequency] = useState(false);
  const [skipping, setSkipping] = useState(false);

  // Reset edit state and load schedule when transaction changes
  useEffect(() => {
    if (transaction) {
      setEditAmount(transaction.amount.toString());
      setEditDescription(transaction.description);
      setEditDate(formatDateForInput(new Date(transaction.date)));
      setEditCategory(transaction.categoryId);
      setEditing(false);
      setShowDeleteConfirm(false);
      
      // Check both scheduledTransactionId AND recurring tags
      const hasSchedule = !!transaction.scheduledTransactionId;
      const hasRecurringTag = transaction.tags?.some(t => t.startsWith('recurring')) || false;
      setIsRecurring(hasSchedule || hasRecurringTag);

      // Load the parent schedule if exists
      if (transaction.scheduledTransactionId) {
        scheduledTransactionService.getById(transaction.scheduledTransactionId)
          .then((sched) => {
            if (sched && !sched.deletedAt) {
              setSchedule(sched);
              setFrequency(sched.frequency);
            } else {
              setSchedule(null);
              setFrequency('monthly');
            }
          })
          .catch(() => {
            setSchedule(null);
            setFrequency('monthly');
          });
      } else {
        setSchedule(null);
        setFrequency('monthly');
      }
    }
  }, [transaction]);

  if (!transaction) return null;

  // Check if this transaction was created from a scheduled transaction
  const isFromSchedule = !!transaction.scheduledTransactionId;
  const isAlreadySkipped = !!transaction.wasSkipped;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(transaction.id!);
      // NOTE: Don't recalculate balances - managed manually via /dev page
      onRefresh();
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount <= 0) return;

    // BUG-040 FIX: Include date in update - triggers BUG-039 fix
    // which updates parent schedule's dayOfMonth for recurring transactions
    // BUG-071 FIX: Include categoryId - triggers parent schedule category update for recurring
    await transactionService.update(transaction.id!, {
      amount,
      description: editDescription,
      date: parseDateAsLocal(editDate),
      categoryId: editCategory,
    });
    // NOTE: Don't recalculate balances - managed manually via /dev page
    onRefresh();
    onClose();
  };

  const handleSkip = async () => {
    if (!transaction.id) return;
    setSkipping(true);
    try {
      await transactionService.update(transaction.id, {
        wasSkipped: true,
      });
      onRefresh();
      onClose();
    } finally {
      setSkipping(false);
    }
  };

  const handleUnskip = async () => {
    if (!transaction.id) return;
    setSkipping(true);
    try {
      await transactionService.update(transaction.id, {
        wasSkipped: false,
      });
      onRefresh();
      onClose();
    } finally {
      setSkipping(false);
    }
  };

  const handleToggleRecurring = async () => {
    if (!transaction.id) return;
    setTogglingRecurring(true);
    try {
      if (isRecurring) {
        // TURNING OFF: Stop recurring
        // If there's a linked schedule, delete it and mark as permanently ignored
        if (schedule) {
          await scheduledTransactionService.update(schedule.id, {
            isIgnoredPermanently: true, // Prevent re-detection
            isPaused: true,
          });
          await scheduledTransactionService.delete(schedule.id);
          setSchedule(null);
        }

        // Remove recurring tags and unlink from schedule
        const currentTags = transaction.tags || [];
        const newTags = currentTags.filter(t => !t.startsWith('recurring'));
        await transactionService.update(transaction.id, {
          tags: newTags.length > 0 ? newTags : undefined,
          scheduledTransactionId: undefined,
        });

        // RECURRING-FIX: Issue 2C - Force sync and set cooldown when stopping recurring
        // This ensures changes reach server before user switches devices
        setBridgeCooldown(15); // Prevent bridge from recreating
        await syncManager.forceImmediatePush();

        setIsRecurring(false);
        console.log('[Edit] Stopped recurring for transaction:', transaction.id);
      } else {
        // TURNING ON: Create new scheduled transaction
        const transactionDate = new Date(transaction.date);
        
        // Calculate next due date based on frequency
        const nextDueDate = new Date(transactionDate);
        switch (frequency) {
          case 'weekly':
            nextDueDate.setDate(nextDueDate.getDate() + 7);
            break;
          case 'biweekly':
            nextDueDate.setDate(nextDueDate.getDate() + 14);
            break;
          case 'monthly':
            nextDueDate.setMonth(nextDueDate.getMonth() + 1);
            break;
          case 'yearly':
            nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);
            break;
        }

        // Create the scheduled transaction
        const scheduleId = await scheduledTransactionService.create({
          accountId: transaction.accountId,
          categoryId: transaction.categoryId,
          type: transaction.type,
          amount: transaction.amount,
          description: transaction.description,
          frequency,
          dayOfMonth: frequency === 'monthly' || frequency === 'yearly'
            ? transactionDate.getDate()
            : undefined,
          dayOfWeek: frequency === 'weekly' || frequency === 'biweekly'
            ? transactionDate.getDay()
            : undefined,
          nextDueDate,
          lastGeneratedDate: transactionDate,
          isPaused: false,
          isIgnoredPermanently: false,
          autoDetected: false, // Manually created
          detectionKey: normalizeDetectionKey(transaction.description),
          dismissCount: 0,
        });

        // Update the transaction to link to the schedule
        const currentTags = transaction.tags || [];
        if (!currentTags.includes('recurring')) {
          currentTags.push('recurring');
        }
        await transactionService.update(transaction.id, {
          tags: currentTags,
          scheduledTransactionId: scheduleId,
        });

        // Load the new schedule
        const newSchedule = await scheduledTransactionService.getById(scheduleId);
        if (newSchedule) {
          setSchedule(newSchedule);
        }

        setIsRecurring(true);
        console.log('[Edit] Created recurring schedule:', scheduleId);
      }
      onRefresh();
    } catch (err) {
      console.error('[Edit] Error toggling recurring:', err);
    } finally {
      setTogglingRecurring(false);
    }
  };

  // Handle frequency change
  const handleFrequencyChange = async (newFrequency: ScheduleFrequency) => {
    if (!transaction.id) return;
    
    setChangingFrequency(true);
    try {
      setFrequency(newFrequency);
      
      // If there's a linked schedule, update its frequency
      if (schedule) {
        const transactionDate = new Date(transaction.date);
        
        // Recalculate next due date based on new frequency
        const nextDueDate = new Date(transactionDate);
        switch (newFrequency) {
          case 'weekly':
            nextDueDate.setDate(nextDueDate.getDate() + 7);
            break;
          case 'biweekly':
            nextDueDate.setDate(nextDueDate.getDate() + 14);
            break;
          case 'monthly':
            nextDueDate.setMonth(nextDueDate.getMonth() + 1);
            break;
          case 'yearly':
            nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);
            break;
        }

        await scheduledTransactionService.update(schedule.id, {
          frequency: newFrequency,
          dayOfMonth: newFrequency === 'monthly' || newFrequency === 'yearly'
            ? transactionDate.getDate()
            : undefined,
          dayOfWeek: newFrequency === 'weekly' || newFrequency === 'biweekly'
            ? transactionDate.getDay()
            : undefined,
          nextDueDate,
        });

        console.log('[Edit] Updated schedule frequency to:', newFrequency);
      }
    } catch (err) {
      console.error('[Edit] Error changing frequency:', err);
    } finally {
      setChangingFrequency(false);
    }
  };

  const formatDate = (date: Date) => {
    return formatDateIntl(new Date(date), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Modal isOpen={!!transaction} onClose={onClose} title={t('transactions.detail.title')}>
      <div className="transaction-actions-modal">
        {!editing ? (
          <>
            {/* View Mode */}
            <div className="txn-details">
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.type')}</span>
                <span className={`txn-detail-value type-${transaction.type}`}>
                  {t(`transactions.type.${transaction.type}`)}
                </span>
              </div>
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.amount')}</span>
                <span className={`txn-detail-value ${transaction.type === 'income' ? 'text-income' : 'text-expense'}`}>
                  {transaction.type === 'income' ? '+' : '-'}
                  {formatCurrencyIntl(transaction.amount, transaction.currency)}
                </span>
              </div>
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.description')}</span>
                <span className="txn-detail-value">{transaction.description || t('transactions.detail.none')}</span>
              </div>
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.category')}</span>
                <span className="txn-detail-value">{category?.name ? categoryDisplayName(category.name) : t('transactions.detail.na')}</span>
              </div>
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.account')}</span>
                <span className="txn-detail-value">{account?.fullName || account?.name || t('transactions.detail.na')}</span>
              </div>
              <div className="txn-detail-row">
                <span className="txn-detail-label">{t('transactions.detail.date')}</span>
                <span className="txn-detail-value">{formatDate(transaction.date)}</span>
              </div>
            </div>

            {/* Skipped Badge */}
            {isAlreadySkipped && (
              <div className="skipped-badge-row">
                <span className="skipped-badge">{t('transactions.detail.skipped')}</span>
                <button
                  className="unskip-link"
                  onClick={handleUnskip}
                  disabled={skipping}
                >
                  {skipping ? t('transactions.detail.restoring') : t('transactions.detail.restore')}
                </button>
              </div>
            )}

            {/* Recurring Toggle + Frequency Selector */}
            <div className="recurring-section-edit">
              <label className="recurring-toggle-row">
                <span className="recurring-label">
                  <span className="recurring-icon">↻</span>
                  {t('transactions.detail.recurring')}
                </span>
                <button
                  className={`toggle-btn ${isRecurring ? 'active' : ''}`}
                  onClick={handleToggleRecurring}
                  disabled={togglingRecurring}
                >
                  <span className="toggle-knob"></span>
                </button>
              </label>

              {/* Frequency selector - shows when recurring is ON */}
              {isRecurring && (
                <div className="frequency-selector-edit">
                  <span className="frequency-label-edit">{t('transactions.detail.repeats')}</span>
                  <div className="frequency-options-edit">
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`frequency-btn-edit ${frequency === opt.value ? 'active' : ''}`}
                        onClick={() => handleFrequencyChange(opt.value)}
                        disabled={changingFrequency}
                      >
                        {t(opt.label)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* RECURRING-FIX: Issue 3 - Explicit "Stop Recurring" button */}
              {/* Shows when transaction is recurring and has a linked schedule */}
              {isRecurring && schedule && (
                <div className="stop-recurring-section">
                  <Button
                    variant="danger"
                    onClick={handleToggleRecurring}
                    disabled={togglingRecurring}
                    className="stop-recurring-btn"
                  >
                    {togglingRecurring ? t('transactions.detail.stopping') : t('transactions.detail.stop_recurring')}
                  </Button>
                  <p className="stop-recurring-note">
                    {t('transactions.detail.stop_note')}
                  </p>
                </div>
              )}
            </div>

            <div className="txn-actions-buttons">
              <Button variant="secondary" onClick={() => setEditing(true)}>
                {t('common.edit')}
              </Button>

              {/* Skip button - only for scheduled transactions that aren't already skipped */}
              {isFromSchedule && !isAlreadySkipped && (
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={skipping}
                  className="skip-btn"
                >
                  {skipping ? t('transactions.detail.skipping') : t('transactions.detail.skip_this')}
                </Button>
              )}

              {!showDeleteConfirm ? (
                <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                  {t('common.delete')}
                </Button>
              ) : (
                <div className="delete-confirm">
                  <p className="delete-warning">{t('transactions.detail.delete_confirm')}</p>
                  <div className="delete-confirm-buttons">
                    <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                      {deleting ? t('accounts.actions.deleting') : t('accounts.actions.yes_delete')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Edit Mode */}
            <div className="txn-edit-form">
              <div className="form-group">
                <label>{t('transactions.detail.amount')}</label>
                <Input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>{t('transactions.detail.date')} {isFromSchedule && <span className="hint">{t('transactions.detail.date_hint')}</span>}</label>
                <Input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('transactions.detail.description')}</label>
                <Input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder={t('transactions.detail.desc_placeholder')}
                />
              </div>
              <div className="form-group">
                <label>
                  {t('transactions.detail.category')}
                  {isFromSchedule && <span className="hint">{t('transactions.detail.category_hint')}</span>}
                </label>
                <select
                  className="category-select"
                  value={editCategory || ''}
                  onChange={(e) => setEditCategory(e.target.value || undefined)}
                >
                  <option value="">{t('transactions.detail.no_category')}</option>
                  {categories
                    .filter((c) => c.isActive && (c.type === transaction.type || transaction.type === 'transfer'))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.icon ? `${c.icon} ` : ''}{categoryDisplayName(c.name)}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="txn-actions-buttons">
              <Button variant="ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveEdit}>
                {t('transactions.detail.save_changes')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ============================================================
// Search Options Modal
// ============================================================

type SearchRangeOption = '7mo' | '14mo' | 'all';

// Labels are catalog keys, resolved with t() at render.
const SEARCH_RANGE_OPTIONS: { value: SearchRangeOption; label: string }[] = [
  { value: '7mo', label: 'transactions.range.mo7' },
  { value: '14mo', label: 'transactions.range.mo14' },
  { value: 'all', label: 'transactions.range.all' },
];

interface SearchOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  accounts: Account[];
  selectedCategory: string;
  selectedAccount: string;
  amountMin: string;
  amountMax: string;
  searchRange: SearchRangeOption;
  onCategoryChange: (id: string) => void;
  onAccountChange: (id: string) => void;
  onAmountMinChange: (val: string) => void;
  onAmountMaxChange: (val: string) => void;
  onSearchRangeChange: (range: SearchRangeOption) => void;
  onClear: () => void;
}

function SearchOptionsModal({
  isOpen,
  onClose,
  categories,
  accounts,
  selectedCategory,
  selectedAccount,
  amountMin,
  amountMax,
  searchRange,
  onCategoryChange,
  onAccountChange,
  onAmountMinChange,
  onAmountMaxChange,
  onSearchRangeChange,
  onClear,
}: SearchOptionsModalProps) {
  const { t } = useLanguage();
  const hasFilters = selectedCategory || selectedAccount || amountMin || amountMax || searchRange !== '7mo';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('transactions.filter.search_options_title')}>
      <div className="search-options-modal">
        {/* Search syntax hint (Discoverability Hints rule, design/WEBSITE.md) */}
        <p className="search-syntax-hint">{t('transactions.filter.search_syntax_hint')}</p>

        {/* Search Range */}
        <div className="filter-group">
          <label className="filter-label">{t('transactions.filter.search_range')}</label>
          <Select
            value={searchRange}
            onChange={(e) => onSearchRangeChange(e.target.value as SearchRangeOption)}
            options={SEARCH_RANGE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
          />
          <p className="filter-hint">{t('transactions.filter.search_range_hint')}</p>
        </div>

        {/* Category Filter */}
        <div className="filter-group">
          <label className="filter-label">{t('transactions.filter.category')}</label>
          <Select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            options={[
              { value: '', label: t('transactions.filter.all_categories') },
              ...categories.map((c) => ({ value: c.id!, label: categoryDisplayName(c.name) })),
            ]}
          />
        </div>

        {/* Account Filter */}
        <div className="filter-group">
          <label className="filter-label">{t('transactions.filter.account')}</label>
          <Select
            value={selectedAccount}
            onChange={(e) => onAccountChange(e.target.value)}
            options={[
              { value: '', label: t('transactions.filter.all_accounts') },
              ...accounts.filter((a) => a.isActive).map((a) => ({
                value: a.id!,
                label: a.fullName || a.name,
              })),
            ]}
          />
        </div>

        {/* Amount Range */}
        <div className="filter-group">
          <label className="filter-label">{t('transactions.filter.amount_range')}</label>
          <div className="amount-range-inputs">
            <Input
              type="number"
              placeholder={t('transactions.filter.min')}
              value={amountMin}
              onChange={(e) => onAmountMinChange(e.target.value)}
              min="0"
              step="0.01"
            />
            <span className="amount-range-separator">{t('transactions.filter.to')}</span>
            <Input
              type="number"
              placeholder={t('transactions.filter.max')}
              value={amountMax}
              onChange={(e) => onAmountMaxChange(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="search-options-actions">
          {hasFilters && (
            <Button variant="ghost" onClick={onClear}>
              {t('transactions.filter.clear_filters')}
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            {t('transactions.filter.apply')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Month Picker Component
// ============================================================

interface MonthPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (year: number, month: number) => void;
  currentSelection?: { year: number; month: number };
}

function MonthPicker({ isOpen, onClose, onSelect, currentSelection }: MonthPickerProps) {
  const { t } = useLanguage();
  const now = new Date();
  const [viewYear, setViewYear] = useState(currentSelection?.year || now.getFullYear());

  // Localized short month names via Intl (year is arbitrary)
  const months = Array.from({ length: 12 }, (_, i) =>
    formatDateIntl(new Date(2000, i, 1), { month: 'short' })
  );

  if (!isOpen) return null;

  return (
    <div className="month-picker-overlay" onClick={onClose}>
      <div className="month-picker" onClick={(e) => e.stopPropagation()}>
        <div className="month-picker-header">
          <button
            className="month-picker-nav"
            onClick={() => setViewYear(viewYear - 1)}
            aria-label={t('transactions.aria.prev_year')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="month-picker-year">{viewYear}</span>
          <button
            className="month-picker-nav"
            onClick={() => setViewYear(viewYear + 1)}
            disabled={viewYear >= now.getFullYear()}
            aria-label={t('transactions.aria.next_year')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
        <div className="month-picker-grid">
          {months.map((month, idx) => {
            const isFuture = viewYear > now.getFullYear() ||
              (viewYear === now.getFullYear() && idx > now.getMonth());
            const isSelected = currentSelection?.year === viewYear && currentSelection?.month === idx;

            return (
              <button
                key={month}
                className={`month-picker-btn ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(viewYear, idx);
                  onClose();
                }}
                disabled={isFuture}
              >
                {month}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Component
// ============================================================

export default function TransactionsPage() {
  const { t, language } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();
  const [searchParams, setSearchParams] = useSearchParams();

  // Basic filters
  // Search initializes from ?q= for deep links (e.g. Analytics watchlist chips)
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Live search: queries fire automatically ~0.4s after typing stops (no
  // Enter needed). The raw keystroke state only drives the input; the
  // debounced value drives the queries so the heavy search-stats query
  // (limit 100000) never fires per keypress.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Account filter - visible dropdown (separate from advanced search account filter)
  // Supports URL param for deep linking from AccountsPage
  const [accountFilterVisible, setAccountFilterVisible] = useState<string>(() => {
    return searchParams.get('accountId') || '';
  });

  // Date range filters - default to saved preference (60d if none saved)
  const [datePreset, setDatePreset] = useState<DateRangePreset>(getSavedDatePreset);
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | undefined>();
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  // Advanced search filters
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  // ?categoryId= deep link (Analytics category drill-down "View in History")
  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('categoryId') || '');
  const [accountFilter, setAccountFilter] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [searchRange, setSearchRange] = useState<SearchRangeOption>('7mo');

  // Data state
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [searchStats, setSearchStats] = useState<SearchInsightStats | null>(null);
  const [searchStatsLoading, setSearchStatsLoading] = useState(false);
  const [searchStatsError, setSearchStatsError] = useState<string | null>(null);

  // Calculate date range from preset (for browsing)
  const dateRange = useMemo(
    () => getDateRangeFromPreset(datePreset, selectedMonth, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datePreset, selectedMonth, language]
  );

  // Calculate search date range (for when search is active)
  const searchDateRange = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    if (searchRange === 'all') {
      return { start: undefined, end: undefined };
    }

    const start = new Date(now);
    if (searchRange === '7mo') {
      start.setMonth(start.getMonth() - 7);
    } else if (searchRange === '14mo') {
      start.setMonth(start.getMonth() - 14);
    }
    start.setHours(0, 0, 0, 0);

    return { start, end };
  }, [searchRange]);

  const amountMinValue = useMemo(() => {
    if (!amountMin.trim()) return undefined;
    const value = Number(amountMin);
    return Number.isFinite(value) ? value : undefined;
  }, [amountMin]);

  const amountMaxValue = useMemo(() => {
    if (!amountMax.trim()) return undefined;
    const value = Number(amountMax);
    return Number.isFinite(value) ? value : undefined;
  }, [amountMax]);

  const trimmedSearch = debouncedSearch.trim();

  // Instant "it's working" feedback: spinner in the search box from the very
  // first keystroke (debounce pending) until the totals query settles.
  const searchBusy =
    Boolean(search.trim()) && (search.trim() !== trimmedSearch || searchStatsLoading);

  const searchRangeLabel = t(
    SEARCH_RANGE_OPTIONS.find((option) => option.value === searchRange)?.label || 'transactions.range.mo7'
  );

  // Raw search terms for highlighting matches in the result list
  // (parsed once per debounced query - whitespace=AND, comma=OR)
  const highlightTerms = useMemo(
    () => (trimmedSearch ? parseSearchQuery(trimmedSearch).rawTerms : undefined),
    [trimmedSearch]
  );

  // '/' focuses the search box (desktop convenience) - ignored while typing
  // in any input/select/textarea or with modifier keys held.
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Check if any advanced filters are active
  const hasAdvancedFilters = Boolean(categoryFilter || accountFilter || amountMin || amountMax || searchRange !== '7mo');

  // Determine effective date range:
  // - When searching: use searchDateRange
  // - When browsing: use dateRange
  const effectiveDateRange = trimmedSearch
    ? searchDateRange
    : { start: dateRange.start, end: dateRange.end };

  // Effective account filter: visible dropdown takes priority over advanced search
  const effectiveAccountFilter = accountFilterVisible || accountFilter || undefined;

  // Fetch transactions with filters
  const { transactions, loading, error, refresh, hasMore, loadMore } = useTransactions({
    search: trimmedSearch || undefined,
    type: typeFilter as 'income' | 'expense' | 'transfer' | undefined,
    categoryId: categoryFilter || undefined,
    accountId: effectiveAccountFilter,
    startDate: effectiveDateRange.start,
    endDate: effectiveDateRange.end,
    amountMin: amountMinValue,
    amountMax: amountMaxValue,
    limit: 100, // Increased limit for better scrolling
  });

  // Fetch summary for stats (uses same date range)
  const { data: summary, loading: summaryLoading } = useSummary({
    startDate: dateRange.start,
    endDate: dateRange.end,
  });

  useEffect(() => {
    let cancelled = false;

    async function loadSearchStats() {
      if (!trimmedSearch) {
        setSearchStats(null);
        setSearchStatsError(null);
        setSearchStatsLoading(false);
        return;
      }

      setSearchStatsLoading(true);
      setSearchStatsError(null);

      try {
        const results = await transactionService.getFiltered({
          search: trimmedSearch,
          type: typeFilter as 'income' | 'expense' | 'transfer' | undefined,
          categoryId: categoryFilter || undefined,
          accountId: effectiveAccountFilter,
          startDate: searchDateRange.start,
          endDate: searchDateRange.end,
          amountMin: amountMinValue,
          amountMax: amountMaxValue,
          limit: 100000,
          offset: 0,
        });

        if (cancelled) return;

        const totals = results.reduce(
          (acc, txn) => {
            if (!isBalanceAdjustment(txn.description)) {
              acc[txn.type] += txn.amount;
            }
            return acc;
          },
          { income: 0, expense: 0, transfer: 0 }
        );
        const dayCount = getRangeDayCount(searchDateRange.start, searchDateRange.end, results);
        const monthCount = Math.max(1, dayCount / 30.4375);
        const biweekCount = Math.max(1, dayCount / 14);

        setSearchStats({
          count: results.length,
          income: totals.income,
          expense: totals.expense,
          transfer: totals.transfer,
          net: totals.income - totals.expense,
          monthlyExpenseAverage: totals.expense / monthCount,
          biweeklyExpenseAverage: totals.expense / biweekCount,
          monthlyIncomeAverage: totals.income / monthCount,
          biweeklyIncomeAverage: totals.income / biweekCount,
          dayCount,
        });
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to calculate search totals';
          setSearchStats(null);
          setSearchStatsError(message);
        }
      } finally {
        if (!cancelled) {
          setSearchStatsLoading(false);
        }
      }
    }

    loadSearchStats();

    return () => {
      cancelled = true;
    };
  }, [
    trimmedSearch,
    typeFilter,
    categoryFilter,
    effectiveAccountFilter,
    searchDateRange.start,
    searchDateRange.end,
    amountMinValue,
    amountMaxValue,
  ]);

  // Load categories and accounts for display
  const loadCategoriesAndAccounts = useCallback(async () => {
    try {
      const [cats, accts] = await Promise.all([
        db.categories.toArray(),
        db.accounts.toArray(),
      ]);
      setCategories(cats);
      setAccounts(accts);
    } catch (err) {
      // Handle NotFoundError when tables don't exist yet (schema upgrade in progress)
      if (err instanceof Error && err.name === 'NotFoundError') {
        console.warn('[TXN] Categories/accounts table not found - schema may be upgrading');
        return;
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    loadCategoriesAndAccounts();
  }, [loadCategoriesAndAccounts]);

  // Subscribe to data events for real-time updates
  useEffect(() => {
    const unsubCategories = dataEvents.on('categories', loadCategoriesAndAccounts);
    const unsubAccounts = dataEvents.on('accounts', loadCategoriesAndAccounts);
    return () => {
      unsubCategories();
      unsubAccounts();
    };
  }, [loadCategoriesAndAccounts]);

  // Helper to get category by ID
  const getCategory = useCallback(
    (id?: string) => categories.find((c) => c.id === id),
    [categories]
  );

  // Helper to get account by ID
  const getAccount = useCallback(
    (id?: string) => accounts.find((a) => a.id === id),
    [accounts]
  );

  // Create Map for OverdueSection (more efficient lookup)
  const accountsMap = useMemo(() => {
    const map = new Map<string, Account>();
    accounts.forEach((a) => { if (a.id) map.set(a.id, a); });
    return map;
  }, [accounts]);

  // Handle transaction click - open actions modal
  const handleTransactionClick = useCallback((transaction: Transaction) => {
    setSelectedTransaction(transaction);
  }, []);

  // Handle delete transaction
  const handleDeleteTransaction = useCallback(async (id: string) => {
    await transactionService.delete(id);
  }, []);

  // Separate upcoming (future) transactions from past
  const { upcomingTransactions, pastTransactions } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const upcoming: Transaction[] = [];
    const past: Transaction[] = [];

    for (const txn of transactions) {
      const txnDate = new Date(txn.date);
      const txnDay = new Date(txnDate.getFullYear(), txnDate.getMonth(), txnDate.getDate());

      if (txnDay > today) {
        upcoming.push(txn);
      } else {
        past.push(txn);
      }
    }

    // Sort upcoming by date ascending (nearest first)
    upcoming.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return { upcomingTransactions: upcoming, pastTransactions: past };
  }, [transactions]);

  // Group past transactions by month and date
  const groupedTransactions = useMemo((): GroupedTransactions[] => {
    if (!pastTransactions.length) return [];

    const monthGroups = new Map<string, Map<string, Transaction[]>>();

    for (const txn of pastTransactions) {
      const date = new Date(txn.date);
      const monthKey = getMonthKey(date);
      const dateKey = getDateKey(date);

      if (!monthGroups.has(monthKey)) {
        monthGroups.set(monthKey, new Map());
      }
      const dayGroups = monthGroups.get(monthKey)!;

      if (!dayGroups.has(dateKey)) {
        dayGroups.set(dateKey, []);
      }
      dayGroups.get(dateKey)!.push(txn);
    }

    // Convert to array structure
    const result: GroupedTransactions[] = [];

    // Sort months descending (newest first)
    const sortedMonths = Array.from(monthGroups.keys()).sort().reverse();

    for (const monthKey of sortedMonths) {
      const dayGroups = monthGroups.get(monthKey)!;
      const days: GroupedTransactions['days'] = [];

      // Sort days descending (newest first)
      const sortedDays = Array.from(dayGroups.keys()).sort().reverse();

      let monthCount = 0;
      for (const dateKey of sortedDays) {
        const txns = dayGroups.get(dateKey)!;
        monthCount += txns.length;

        // Parse date for label
        const [y, m, d] = dateKey.split('-').map(Number);
        const date = new Date(y, m - 1, d);

        days.push({
          dateKey,
          dateLabel: formatDateHeader(date, t),
          transactions: txns,
        });
      }

      result.push({
        monthKey,
        monthLabel: formatMonthHeader(monthKey),
        count: monthCount,
        days,
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastTransactions, language]);

  /**
   * Format upcoming date for display.
   */
  const formatUpcomingDate = useCallback((date: Date): string => {
    return formatMonthDay(new Date(date));
  }, []);

  /**
   * Get background color for upcoming icon based on transaction type.
   */
  const getUpcomingIconBg = useCallback((type: string): string => {
    switch (type) {
      case 'income':
        return 'rgba(0, 214, 143, 0.15)'; // Income green
      case 'expense':
        return 'rgba(255, 107, 107, 0.15)'; // Expense red
      case 'transfer':
        return 'rgba(78, 205, 196, 0.15)'; // Transfer teal
      default:
        return 'rgba(245, 158, 11, 0.15)'; // Warning amber
    }
  }, []);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const nearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
      if (nearBottom && hasMore && !loading) {
        loadMore();
      }
    },
    [hasMore, loading, loadMore]
  );

  // Handle retry on error
  const handleRetry = () => {
    refresh();
  };

  // Handle clearing advanced filters
  const handleClearAdvancedFilters = useCallback(() => {
    setCategoryFilter('');
    setAccountFilter('');
    setAmountMin('');
    setAmountMax('');
    setSearchRange('7mo');
  }, []);

  // Handle visible account filter change (URL sync happens in the effect below)
  const handleAccountFilterChange = useCallback((accountId: string) => {
    setAccountFilterVisible(accountId);
  }, []);

  // Keep deep-linkable filters in the URL (replace, not push - no history
  // spam while typing). Merging into the CURRENT params also fixes the old
  // bug where changing the account dropdown wiped ?q= / ?categoryId=.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string) => {
      if (value) params.set(key, value);
      else params.delete(key);
    };
    setOrDelete('q', trimmedSearch);
    setOrDelete('accountId', accountFilterVisible);
    setOrDelete('categoryId', categoryFilter);
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [trimmedSearch, accountFilterVisible, categoryFilter, searchParams, setSearchParams]);

  // Handle month selection
  const handleSelectMonth = useCallback((year: number, month: number) => {
    setSelectedMonth({ year, month });
    setDatePreset('month');
  }, []);

  // Handle preset selection
  const handlePresetClick = useCallback((preset: DateRangePreset) => {
    setDatePreset(preset);
    saveDatePreset(preset);  // Remember preference
    if (preset !== 'month') {
      setSelectedMonth(undefined);
    }
  }, []);

  // Navigate to previous month
  const handlePrevMonth = useCallback(() => {
    const current = selectedMonth || { year: new Date().getFullYear(), month: new Date().getMonth() };
    let newMonth = current.month - 1;
    let newYear = current.year;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    setSelectedMonth({ year: newYear, month: newMonth });
    setDatePreset('month');
  }, [selectedMonth]);

  // Navigate to next month
  const handleNextMonth = useCallback(() => {
    const now = new Date();
    const current = selectedMonth || { year: now.getFullYear(), month: now.getMonth() };
    let newMonth = current.month + 1;
    let newYear = current.year;
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    // Don't go past current month
    if (newYear > now.getFullYear() || (newYear === now.getFullYear() && newMonth > now.getMonth())) {
      return;
    }
    setSelectedMonth({ year: newYear, month: newMonth });
    setDatePreset('month');
  }, [selectedMonth]);

  // Check if next month is disabled (can't go past current month)
  const isNextMonthDisabled = useMemo(() => {
    const now = new Date();
    const current = selectedMonth || { year: now.getFullYear(), month: now.getMonth() };
    let nextMonth = current.month + 1;
    let nextYear = current.year;
    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear++;
    }
    return nextYear > now.getFullYear() || (nextYear === now.getFullYear() && nextMonth > now.getMonth());
  }, [selectedMonth]);

  // Error state
  if (error) {
    return (
      <div className="page-error">
        <h2>{t('transactions.load_failed')}</h2>
        <p>{error}</p>
        <button onClick={handleRetry} className="btn btn-primary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">{t('transactions.title')}</h1>
        <div className="page-header-right">
          <span className="date-range-label">{dateRange.label}</span>
          <PrivacyToggle />
        </div>
      </div>

      {/* Date Range Controls */}
      <div className="date-range-controls">
        <div className="date-presets">
          {/* Month Navigation */}
          <button
            className="date-nav-btn"
            onClick={handlePrevMonth}
            aria-label={t('transactions.aria.prev_month')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            className="date-nav-btn"
            onClick={handleNextMonth}
            disabled={isNextMonthDisabled}
            aria-label={t('transactions.aria.next_month')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value}
              className={`date-preset-btn ${datePreset === preset.value ? 'active' : ''}`}
              onClick={() => handlePresetClick(preset.value)}
            >
              {t(preset.label)}
            </button>
          ))}

          <button
            className={`date-preset-btn month-btn ${datePreset === 'month' ? 'active' : ''}`}
            onClick={() => setShowMonthPicker(true)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {datePreset === 'month' ? selectedMonth
              ? formatMonthAxis(new Date(selectedMonth.year, selectedMonth.month), true)
              : t('transactions.range.month')
              : t('transactions.range.month')}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-bar">
        <div className="search-input-wrap">
          <Input
            ref={searchInputRef}
            type="search"
            placeholder={t('transactions.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && search) {
                e.preventDefault();
                setSearch('');
              }
            }}
            className="search-input"
          />
          {search && (
            searchBusy ? (
              <span className="search-spinner" aria-hidden="true" />
            ) : (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearch('');
                  searchInputRef.current?.focus();
                }}
                aria-label={t('transactions.aria.clear_search')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )
          )}
        </div>
        <label className={`extended-search-toggle ${searchRange === '14mo' ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={searchRange === '14mo'}
            onChange={(e) => setSearchRange(e.target.checked ? '14mo' : '7mo')}
          />
          <span>{t('transactions.range.ext_14mo')}</span>
        </label>
        <button
          className={`settings-btn ${hasAdvancedFilters ? 'has-filters' : ''}`}
          onClick={() => setShowSearchOptions(true)}
          aria-label={t('transactions.filter.search_options_aria')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          {hasAdvancedFilters && <span className="filter-badge" />}
        </button>
      </div>

      {/* Filter Row - Type & Account Dropdowns */}
      <div className="filter-row">
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={[
            { value: '', label: t('transactions.filter.all_types') },
            { value: 'income', label: t('transactions.type.income') },
            { value: 'expense', label: t('transactions.type.expense') },
            { value: 'transfer', label: t('transactions.type.transfer') },
          ]}
          className="type-select"
        />
        <Select
          value={accountFilterVisible}
          onChange={(e) => handleAccountFilterChange(e.target.value)}
          options={[
            { value: '', label: t('transactions.filter.all_accounts') },
            ...accounts.filter((a) => a.isActive).map((a) => ({
              value: a.id!,
              label: a.fullName || a.name,
            })),
          ]}
          className="account-select"
        />
        {(hasAdvancedFilters || search || accountFilterVisible) && (
          <button
            className="clear-filters-btn"
            onClick={() => {
              handleClearAdvancedFilters();
              handleAccountFilterChange('');
              setSearch('');
            }}
          >
            {t('transactions.filter.clear_all')}
          </button>
        )}
      </div>

      {/* Active advanced-filter chips - filters set in the Search Options
          modal are otherwise invisible (just a dot on the funnel icon).
          Each chip names its filter; tapping removes it. */}
      {hasAdvancedFilters && (
        <div className="filter-chips">
          {categoryFilter && (
            <button
              className="filter-chip"
              onClick={() => setCategoryFilter('')}
              aria-label={t('transactions.chips.remove_aria')}
            >
              {t('transactions.chips.category', {
                name: categoryDisplayName(getCategory(categoryFilter)?.name) || t('transactions.filter.category'),
              })}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          {accountFilter && (
            <button
              className="filter-chip"
              onClick={() => setAccountFilter('')}
              aria-label={t('transactions.chips.remove_aria')}
            >
              {t('transactions.chips.account', {
                name: getAccount(accountFilter)?.fullName || getAccount(accountFilter)?.name || t('transactions.filter.account'),
              })}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          {amountMin && (
            <button
              className="filter-chip"
              onClick={() => setAmountMin('')}
              aria-label={t('transactions.chips.remove_aria')}
            >
              {t('transactions.chips.min', { amount: amountMin })}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          {amountMax && (
            <button
              className="filter-chip"
              onClick={() => setAmountMax('')}
              aria-label={t('transactions.chips.remove_aria')}
            >
              {t('transactions.chips.max', { amount: amountMax })}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          {searchRange !== '7mo' && (
            <button
              className="filter-chip"
              onClick={() => setSearchRange('7mo')}
              aria-label={t('transactions.chips.remove_aria')}
            >
              {t('transactions.chips.range', { range: searchRangeLabel })}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Search Totals */}
      {trimmedSearch && (
        <div className="search-insights">
          <div className="search-insights-header">
            <div>
              <span className="search-insights-title">{t('transactions.totals.title')}</span>
              <span className="search-insights-subtitle">
                {t('transactions.totals.subtitle', { query: trimmedSearch, range: searchRangeLabel })}
              </span>
            </div>
            <span className="search-insights-count">
              {searchStatsLoading
                ? t('transactions.totals.calculating')
                : t('transactions.totals.matches', { count: searchStats?.count || 0 })}
            </span>
          </div>

          {searchStatsError ? (
            <div className="search-insights-error">{searchStatsError}</div>
          ) : (
            <div className="search-insights-grid">
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.total_expense')}</span>
                <strong className="search-insight-value amount--expense">
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? <span className="private-amount masked">-••••</span> : `-${formatCurrency(searchStats?.expense || 0)}`
                  )}
                </strong>
              </div>
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.monthly_avg_expense')}</span>
                <strong className="search-insight-value amount--expense">
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? <span className="private-amount masked">-••••</span> : `-${formatCurrency(searchStats?.monthlyExpenseAverage || 0)}`
                  )}
                </strong>
              </div>
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.biweekly_avg_expense')}</span>
                <strong className="search-insight-value amount--expense">
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? <span className="private-amount masked">-••••</span> : `-${formatCurrency(searchStats?.biweeklyExpenseAverage || 0)}`
                  )}
                </strong>
              </div>
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.total_income')}</span>
                <strong className="search-insight-value amount--income">
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? <span className="private-amount masked">+••••</span> : `+${formatCurrency(searchStats?.income || 0)}`
                  )}
                </strong>
              </div>
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.monthly_avg_income')}</span>
                <strong className="search-insight-value amount--income">
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? <span className="private-amount masked">+••••</span> : `+${formatCurrency(searchStats?.monthlyIncomeAverage || 0)}`
                  )}
                </strong>
              </div>
              <div className="search-insight-card">
                <span className="search-insight-label">{t('transactions.totals.net')}</span>
                <strong className={`search-insight-value ${(searchStats?.net || 0) >= 0 ? 'amount--income' : 'amount--expense'}`}>
                  {searchStatsLoading ? '--' : (
                    isPrivacyMode ? (
                      <span className="private-amount masked">••••</span>
                    ) : (
                      `${(searchStats?.net || 0) >= 0 ? '+' : ''}${formatCurrency(searchStats?.net || 0)}`
                    )
                  )}
                </strong>
              </div>
              {(searchStats?.transfer || 0) > 0 && (
                <div className="search-insight-card">
                  <span className="search-insight-label">{t('transactions.totals.transfers')}</span>
                  <strong className="search-insight-value amount--transfer">
                    {searchStatsLoading ? '--' : (
                      isPrivacyMode ? <span className="private-amount masked">••••</span> : formatCurrency(searchStats?.transfer || 0)
                    )}
                  </strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="summary-bar summary-bar-3">
        <div className="stat-card">
          <div className="stat-label">{t('transactions.stats.income')}</div>
          <div className="stat-value amount--income">
            {summaryLoading ? '--' : (
              isPrivacyMode ? (
                <span className="private-amount masked">+••••</span>
              ) : (
                `+${formatCurrency(summary?.monthIncome || 0)}`
              )
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('transactions.stats.expenses')}</div>
          <div className="stat-value amount--expense">
            {summaryLoading ? '--' : (
              isPrivacyMode ? (
                <span className="private-amount masked">-••••</span>
              ) : (
                `-${formatCurrency(summary?.monthExpense || 0)}`
              )
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('transactions.stats.net')}</div>
          <div className={`stat-value ${(summary?.monthNet || 0) >= 0 ? 'amount--income' : 'amount--expense'}`}>
            {summaryLoading ? '--' : (
              isPrivacyMode ? (
                <span className="private-amount masked">••••</span>
              ) : (
                `${(summary?.monthNet || 0) >= 0 ? '+' : ''}${formatCurrency(summary?.monthNet || 0)}`
              )
            )}
          </div>
        </div>
      </div>

      {/* Overdue Section - Shows scheduled items past their due date */}
      <OverdueSection accounts={accountsMap} />

      {/* Upcoming Section */}
      {upcomingTransactions.length > 0 && (
        <div className="upcoming-section">
          <div className="upcoming-header">
            <span className="upcoming-label">{t('transactions.upcoming.label')}</span>
            <button
              className="upcoming-toggle"
              onClick={() => setShowUpcoming(!showUpcoming)}
            >
              {showUpcoming ? t('transactions.upcoming.hide') : t('transactions.upcoming.show')}
            </button>
          </div>
          {showUpcoming && (
            <div className="upcoming-card">
              {upcomingTransactions.slice(0, 5).map((txn) => (
                <div key={txn.id} className="upcoming-item">
                  <div
                    className="upcoming-icon"
                    style={{ background: getUpcomingIconBg(txn.type) }}
                  >
                    {getCategory(txn.categoryId)?.icon || '📅'}
                  </div>
                  <div className="upcoming-info">
                    <span className="upcoming-name">{txn.description || t('transactions.upcoming.no_description')}</span>
                    <span className="upcoming-meta">
                      {formatUpcomingDate(txn.date)} · {categoryDisplayName(getCategory(txn.categoryId)?.name) || t(`transactions.type.${txn.type}`)} · {getAccount(txn.accountId)?.name}
                    </span>
                  </div>
                  <span className={`upcoming-amount ${txn.type === 'income' ? 'text-income' : txn.type === 'expense' ? 'text-expense' : 'text-transfer'}`}>
                    {txn.type === 'income' ? '+' : txn.type === 'expense' ? '-' : ''}
                    {formatCurrencyIntl(txn.amount, txn.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transaction List */}
      <div className="transactions-list" onScroll={handleScroll}>
        {loading && transactions.length === 0 ? (
          // Initial loading
          Array.from({ length: 8 }).map((_, i) => (
            <TransactionItemSkeleton key={i} />
          ))
        ) : transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>{t('transactions.empty_title')}</h3>
            <p>
              {search
                ? t('transactions.empty_search_hint')
                : t('transactions.empty_add_hint')}
            </p>
          </div>
        ) : (
          <>
            {groupedTransactions.map((month) => (
              <div key={month.monthKey} className="month-group">
                {/* Month Header */}
                <div className="month-header">
                  <span className="month-label">{month.monthLabel}</span>
                  <span className="month-count">{t('transactions.month_count', { count: month.count })}</span>
                </div>

                {/* Day Groups */}
                <div className="month-card">
                  {month.days.map((day) => (
                    <div key={day.dateKey} className="day-group">
                      {/* Date Header */}
                      <div className="date-header">{day.dateLabel}</div>

                      {/* Transactions for this day */}
                      {day.transactions.map((transaction) => (
                        <TransactionItem
                          key={transaction.id}
                          transaction={transaction}
                          category={getCategory(transaction.categoryId)}
                          account={getAccount(transaction.accountId)}
                          toAccount={getAccount(transaction.toAccountId)}
                          onClick={() => handleTransactionClick(transaction)}
                          highlightTerms={highlightTerms}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Loading more indicator */}
            {loading && transactions.length > 0 && (
              <div className="loading-more">
                <TransactionItemSkeleton />
                <TransactionItemSkeleton />
              </div>
            )}
            {!hasMore && transactions.length > 0 && (
              <div className="end-of-list">{t('transactions.no_more')}</div>
            )}
          </>
        )}
      </div>

      {/* Transaction Actions Modal */}
      <TransactionActionsModal
        transaction={selectedTransaction}
        category={getCategory(selectedTransaction?.categoryId)}
        account={getAccount(selectedTransaction?.accountId)}
        categories={categories}
        onClose={() => setSelectedTransaction(null)}
        onDelete={handleDeleteTransaction}
        onRefresh={refresh}
      />

      {/* Search Options Modal */}
      <SearchOptionsModal
        isOpen={showSearchOptions}
        onClose={() => setShowSearchOptions(false)}
        categories={categories}
        accounts={accounts}
        selectedCategory={categoryFilter}
        selectedAccount={accountFilter}
        amountMin={amountMin}
        amountMax={amountMax}
        searchRange={searchRange}
        onCategoryChange={setCategoryFilter}
        onAccountChange={setAccountFilter}
        onAmountMinChange={setAmountMin}
        onAmountMaxChange={setAmountMax}
        onSearchRangeChange={setSearchRange}
        onClear={handleClearAdvancedFilters}
      />

      {/* Month Picker */}
      <MonthPicker
        isOpen={showMonthPicker}
        onClose={() => setShowMonthPicker(false)}
        onSelect={handleSelectMonth}
        currentSelection={selectedMonth}
      />
    </div>
  );
}
